import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, MoreHorizontal, Loader2, Search, Trash2, FolderTree } from 'lucide-react';

import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/data-table/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import {
  useAddressBooks, useAddressBooksAll, useCreateAddressBook, useUpdateAddressBook, useDeleteAddressBook,
  type AddressBook,
} from '@/hooks/use-address-books';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';
import { relativeTime, shortId } from '@/lib/utils';

export const Route = createFileRoute('/_app/address-books')({
  component: AddressBooksPage,
});

function AddressBooksPage() {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [editing, setEditing] = React.useState<AddressBook | null>(null);
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useAddressBooks({ page, pageSize: 25, search: debouncedSearch || undefined });

  return (
    <>
      <PageHeader
        title="Address books"
        description="Group devices by end customer for easier navigation."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /><span>New address book</span>
          </Button>
        }
      />

      <Card className="mb-4 p-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search address books"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8"
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Parent</TableHead>
              <TableHead>Devices</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>ID</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="h-32 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                <FolderTree className="mx-auto h-5 w-5 mb-2 opacity-50" />
                No address books yet. Create one to start organizing devices.
              </TableCell></TableRow>
            ) : (
              data?.data.map((ab) => (
                <TableRow key={ab.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{ab.name}</span>
                      {ab.description && <span className="text-xs text-muted-foreground line-clamp-1">{ab.description}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {ab.parent ? (
                      <Badge variant="outline">{ab.parent.name}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums">{ab._count.devices}</TableCell>
                  <TableCell className="text-sm text-muted-foreground tabular-nums">{relativeTime(ab.createdAt)}</TableCell>
                  <TableCell><span className="id-pill">{shortId(ab.id)}</span></TableCell>
                  <TableCell>
                    <AddressBookRowMenu addressBook={ab} onEdit={() => setEditing(ab)} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {data && (
          <div className="border-t">
            <Pagination page={page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <AddressBookDialog open={creating} onOpenChange={setCreating} mode="create" />
      <AddressBookDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} mode="edit" addressBook={editing} />
    </>
  );
}

function AddressBookRowMenu({ addressBook, onEdit }: { addressBook: AddressBook; onEdit: () => void }) {
  const deleteMut = useDeleteAddressBook();

  function handleDelete(): void {
    const msg = addressBook._count.devices > 0
      ? `Delete "${addressBook.name}"? ${addressBook._count.devices} device${addressBook._count.devices > 1 ? 's' : ''} will become unassigned.`
      : `Delete "${addressBook.name}"?`;
    if (!window.confirm(msg)) return;
    deleteMut.mutate(addressBook.id, {
      onSuccess: () => toast({ title: 'Address book deleted' }),
      onError: (err) => toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: err instanceof ApiError ? err.message : 'Try again',
      }),
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon"><MoreHorizontal /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32">
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        <DropdownMenuItem destructive onSelect={handleDelete}>
          <Trash2 /><span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const abSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  parentId: z.string().optional(),
});
type AbValues = z.infer<typeof abSchema>;

function AddressBookDialog({
  open, onOpenChange, mode, addressBook,
}: {
  open: boolean; onOpenChange: (o: boolean) => void; mode: 'create' | 'edit'; addressBook?: AddressBook | null;
}) {
  const create = useCreateAddressBook();
  const update = useUpdateAddressBook();
  const all = useAddressBooksAll();
  const form = useForm<AbValues>({ resolver: zodResolver(abSchema) });

  React.useEffect(() => {
    if (open) {
      form.reset({
        name: addressBook?.name ?? '',
        description: addressBook?.description ?? '',
        parentId: addressBook?.parentId ?? '__none__',
      });
    }
  }, [open, addressBook, form]);

  function onSubmit(values: AbValues): void {
    const input = {
      name: values.name,
      description: values.description || undefined,
      parentId: values.parentId === '__none__' || !values.parentId ? null : values.parentId,
    };

    const onSuccess = (): void => {
      toast({ title: mode === 'create' ? 'Address book created' : 'Address book updated' });
      onOpenChange(false);
    };
    const onError = (err: unknown): void => toast({
      variant: 'destructive',
      title: mode === 'create' ? 'Could not create' : 'Could not update',
      description: err instanceof ApiError ? err.message : 'Try again',
    });

    if (mode === 'create') {
      create.mutate(input, { onSuccess, onError });
    } else if (addressBook) {
      update.mutate({ id: addressBook.id, input }, { onSuccess, onError });
    }
  }

  const pending = create.isPending || update.isPending;
  // Exclude self from parent options to avoid cycles
  const parentOptions = (all.data?.data ?? []).filter((ab) => ab.id !== addressBook?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New address book' : 'Edit address book'}</DialogTitle>
          <DialogDescription>
            Organize devices by customer, site, or any logical grouping.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ab-name">Name</Label>
            <Input id="ab-name" placeholder="Bakery Inc." autoFocus {...form.register('name')} />
            {form.formState.errors.name && <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="ab-desc">Description</Label>
            <Input id="ab-desc" placeholder="Optional" {...form.register('description')} />
          </div>
          <div className="space-y-2">
            <Label>Parent address book</Label>
            <Select
              value={form.watch('parentId') ?? '__none__'}
              onValueChange={(v) => form.setValue('parentId', v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No parent (top-level) —</SelectItem>
                {parentOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="animate-spin" />}
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
